import React, { FC, useMemo } from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import DateTimeHover from '../date_time_component/DateTime';
import LocationName from '../name_component/location/LocationNameComponent';
import { UserName } from '../name_component/user/UserNameComponent';
import WalkthroughTaskListScoreDisplay from '../walkthrough_score_display/WalkthroughTaskListScoreDisplay';
import { getAnswerItems } from '../../utils/TaskItemUtils';
import { KeyValueDisplay } from '../key_value_display/KeyValueDisplay';

interface WalkthroughDetailsDisplayProps {
	walkthrough: any;
	horizontal?: boolean;
	keyWidth?: number;
}

export const WalkthroughDetailsDisplay: FC<WalkthroughDetailsDisplayProps> = ({
	walkthrough,
	horizontal,
	keyWidth,
}) => {
	const timeZoneId = nullSafeGet('location.timeZoneId', walkthrough);
	const location = nullSafeGet('location', walkthrough);

	const details = useMemo(() => {
		const renderTimestamp = (path: string) =>
			nullSafeGet(path, walkthrough) ? (
				<DateTimeHover timeZone={timeZoneId} timestamp={nullSafeGet(path, walkthrough)} />
			) : null;

		const taskListAvailable =
			nullSafeGetOrElse('taskAnswers', walkthrough, []).flatMap(getAnswerItems).length > 0;

		return {
			'Walkthrough ID': `# ${nullSafeGet('id', walkthrough)}`,
			'Site Survey': nullSafeGet('siteSurvey.title', walkthrough),
			Location: location ? (
				<LocationName mode={'inline'} location={location} placement={'top'} />
			) : null,
			Created: renderTimestamp('createdAt'),
			'Started At': renderTimestamp('startTime'),
			Due: renderTimestamp('dueAt'),
			'Ended At': renderTimestamp('endTime'),
			Completed:
				nullSafeGet('status', walkthrough) === 'complete' ? renderTimestamp('completedAt') : null,
			'Completed By': nullSafeGet('completedByContact', walkthrough) ? (
				<UserName
					contact={nullSafeGet('completedByContact', walkthrough)}
					mode={'inline'}
					placement={'top'}
				/>
			) : null,
			Tasks: taskListAvailable ? (
				<WalkthroughTaskListScoreDisplay walkthrough={walkthrough} />
			) : null,
		};
	}, [walkthrough, timeZoneId, location]);

	const populatedDetails = Object.keys(details).reduce((accumulator, key) => {
		const value = details[key];
		if (value !== null && value !== undefined && value !== '') {
			accumulator[key] = value;
		}
		return accumulator;
	}, {} as Record<string, any>);

	return (
		<KeyValueDisplay horizontal={horizontal} keyWidth={keyWidth} keyValueStore={populatedDetails} />
	);
};

export default WalkthroughDetailsDisplay;
